const cleanText = (value) => String(value ?? "").trim();

export const isActivePendingFlag = (value) => {
  if (typeof value === "boolean") return value;

  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0;
  }

  const normalized = cleanText(value).toLowerCase();
  if (!normalized) return false;

  if (["false", "0", "no", "off", "done", "complete", "completed", "null", "undefined"].includes(normalized)) {
    return false;
  }

  const numericValue = Number(normalized);
  if (Number.isFinite(numericValue)) {
    return numericValue > 0;
  }

  return true;
};

export const normalizeComparablePersonName = (value) => cleanText(value).toLowerCase();

export const areSamePersonNames = (left, right) => {
  const normalizedLeft = normalizeComparablePersonName(left);
  const normalizedRight = normalizeComparablePersonName(right);
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
};

export const getRecruiterOwnerName = (row = {}) =>
  cleanText(row?.owner || row?.assigned_team_member || row?.owner_name || row?.recruiter_name || row?.team_member);

export const getDistinctJobHiringManagerName = (row = {}) => {
  const recruiterName = getRecruiterOwnerName(row);
  const candidates = [
    row?.contact_name,
    row?.hiring_manager_name,
    row?.hiring_manager,
    row?.primary_contact,
  ];

  for (const candidate of candidates) {
    const name = cleanText(candidate);
    if (name && !areSamePersonNames(name, recruiterName)) {
      return name;
    }
  }

  return "";
};

export const sanitizeJobCompanyContactFields = (row = {}) => {
  const recruiterName = getRecruiterOwnerName(row);
  const rawHiringManagerName = cleanText(row?.hiring_manager_name || row?.hiring_manager);
  const rawContactName = cleanText(row?.contact_name);
  const rawPrimaryContact = cleanText(row?.primary_contact);

  const hiringManagerName =
    rawHiringManagerName && !areSamePersonNames(rawHiringManagerName, recruiterName)
      ? rawHiringManagerName
      : "";
  const contactName =
    rawContactName && !areSamePersonNames(rawContactName, recruiterName)
      ? rawContactName
      : hiringManagerName;
  const primaryContact =
    rawPrimaryContact && !areSamePersonNames(rawPrimaryContact, recruiterName)
      ? rawPrimaryContact
      : contactName || hiringManagerName;

  return {
    ...row,
    company_name: cleanText(row?.company_name || row?.linked_company_name),
    company_logo: cleanText(
      row?.company_logo ||
        row?.company_logo_url ||
        row?.linked_company_logo ||
        row?.linked_company_logo_url ||
        row?.logo ||
        row?.entity_logo
    ),
    company_website: cleanText(
      row?.company_website ||
        row?.website ||
        row?.website_url ||
        row?.linked_company_website
    ),
    company_career_website: cleanText(
      row?.company_career_website ||
        row?.career_website ||
        row?.post_link ||
        row?.job_url ||
        row?.linked_company_career_website
    ),
    company_industry: cleanText(
      row?.company_industry ||
        row?.industry ||
        row?.industry_sector ||
        row?.linked_company_industry
    ),
    company_size: cleanText(
      row?.company_size ||
        row?.size_range ||
        row?.linked_company_size
    ),
    contact_repair_pending: isActivePendingFlag(row?.contact_repair_pending),
    job_details_enrichment_pending: isActivePendingFlag(
      row?.job_details_enrichment_pending
    ),
    hiring_manager_name: hiringManagerName,
    hiring_manager: hiringManagerName,
    contact_name: contactName,
    primary_contact: primaryContact,
  };
};

export const isRecruiterContact = (contact = {}) => {
  const role = normalizeComparablePersonName(contact?.role || contact?.title || contact?.position);
  const contactKind = normalizeComparablePersonName(contact?.contact_kind);
  return contactKind.includes("recruiter") || role.includes("recruiter") || role.includes("owner");
};
